import React, { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import BCHeader from "../../shared/BCHeader";
import { HStack, VStack } from "../../components/utils";
import {
  useManagerDashboard,
  useManagerTeam,
} from "../../queries/managerPortal";
import { usePermissionStore } from "../../store/permissionStore";
import { determineUserAccessToResource } from "../../utils/auth";
import { Action, Resource } from "../../utils/interfaces/Companies";
import { TeamMembersGrid } from "./TeamMembersGrid";
import { ManagerGrantsGrid } from "./ManagerGrantsGrid";
import { SegmentedTabs } from "../../components/shared/SegmentedTabs";

interface StatCardProps {
  label: string;
  value: string | number;
  icon: string;
  theme: "blue" | "emerald" | "orange" | "rose";
}

function StatCard({ label, value, icon, theme }: StatCardProps) {
  const themes = {
    blue: {
      iconBg: "bg-blue-50 text-blue-600 border border-blue-100",
      accent: "hover:border-blue-300/60 hover:shadow-blue-50/50",
    },
    emerald: {
      iconBg: "bg-emerald-50 text-emerald-600 border border-emerald-100",
      accent: "hover:border-emerald-300/60 hover:shadow-emerald-50/50",
    },
    orange: {
      iconBg: "bg-orange-50 text-orange-600 border border-orange-100",
      accent: "hover:border-orange-300/60 hover:shadow-orange-50/50",
    },
    rose: {
      iconBg: "bg-rose-50 text-rose-600 border border-rose-100",
      accent: "hover:border-rose-300/60 hover:shadow-rose-50/50",
    },
  };

  const selectedTheme = themes[theme] ?? themes.blue;

  return (
    <VStack
      className={`flex-1 gap-4 p-5 bg-white rounded-xl shadow-sm border border-gray-100 min-w-[180px] transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 ${selectedTheme.accent}`}
    >
      <HStack className="justify-between items-start w-full">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${selectedTheme.iconBg}`}
        >
          <Icon icon={icon} width={20} height={20} />
        </div>
      </HStack>
      <VStack className="gap-1 mt-1">
        <p className="text-[10px] uppercase font-bold tracking-wider text-gray-400 leading-none">
          {label}
        </p>
        <p className="text-3xl font-extrabold text-gray-800 tracking-tight leading-tight">
          {value ?? "-"}
        </p>
      </VStack>
    </VStack>
  );
}

export default function ManagerDashboard() {
  const { permission } = usePermissionStore();
  const { data: dashboard, isLoading: dashLoading } = useManagerDashboard();
  const { data: team = [], isLoading: teamLoading } = useManagerTeam();
  const [activeView, setActiveView] = useState<"employees" | "grants">(
    "employees"
  );
  const [employeeIdToOpen, setEmployeeIdToOpen] = useState<string | null>(null);

  const canViewEmployees = determineUserAccessToResource(
    permission?.aclList || [],
    Resource.Employee,
    Action.Read
  );

  const canViewReports = determineUserAccessToResource(
    permission?.aclList || [],
    Resource.Document,
    Action.Read
  );
  const pendingActionsCount = useMemo(
    () =>
      team.reduce(
        (count, employee) =>
          count +
          (employee.grants ?? []).filter(
            (grant: any) =>
              grant.state === "FOR_APPROVAL" && grant.isPendingApprover
          ).length,
        0
      ),
    [team]
  );

  return (
    <VStack className="gap-6 px-6 py-4">
      <BCHeader
        bcTitle="My Team"
        bcDescription="View and manage your team's grant details"
      />

      {canViewEmployees && (
        <>
          <HStack className="gap-4 flex-wrap">
            <StatCard
              label="Team Members"
              value={dashLoading ? "..." : dashboard?.teamSize ?? 0}
              icon="heroicons:user-group"
              theme="blue"
            />
            <StatCard
              label="Active Grants"
              value={dashLoading ? "..." : dashboard?.totalGrants ?? 0}
              icon="icon-park-outline:certificate"
              theme="emerald"
            />
            <StatCard
              label="Total Vested"
              value={
                dashLoading
                  ? "..."
                  : (dashboard?.totalVested ?? 0).toLocaleString()
              }
              icon="entypo:bar-graph"
              theme="orange"
            />
            <StatCard
              label="Pending Actions"
              value={teamLoading ? "..." : pendingActionsCount}
              icon="mdi:clock-outline"
              theme="rose"
            />
          </HStack>

          <VStack className={`gap-4 ${teamLoading ? "loading" : ""}`}>
            <SegmentedTabs
              activeTab={activeView}
              onChange={setActiveView}
              tabs={[
                { id: "employees", label: "Employees" },
                {
                  id: "grants",
                  label: (
                    <>
                      Grants
                      {pendingActionsCount > 0 && (
                        <span
                          className="ml-2 inline-block h-2 w-2 rounded-full bg-orange-500"
                          style={{
                            animation:
                              "manager-action-required-pulse 2.2s ease-in-out infinite",
                          }}
                          title={`${pendingActionsCount} grant action${
                            pendingActionsCount === 1 ? "" : "s"
                          } required`}
                        />
                      )}
                    </>
                  ),
                },
              ]}
            />
            {activeView === "employees" ? (
              <TeamMembersGrid
                members={team}
                employeeIdToOpen={employeeIdToOpen}
                onEmployeeGrantsOpened={() => setEmployeeIdToOpen(null)}
              />
            ) : (
              <ManagerGrantsGrid
                members={team}
                onViewEmployeeGrants={(employeeId) => {
                  setEmployeeIdToOpen(employeeId);
                  setActiveView("employees");
                }}
              />
            )}
          </VStack>
        </>
      )}
    </VStack>
  );
}
